import { h } from 'preact';
import { useState, useEffect, useCallback } from 'preact/hooks';
import { api } from '../state/auth.js';
import Card from '../components/shared/Card.jsx';
import Button from '../components/shared/Button.jsx';
import { parseServerDate } from '../utils/dates.js';
import { useAuth } from '../hooks/useAuth.js';
import { fetchFeatures } from '../utils/features.js';
import { GOAL_STATUS, measurePct } from './GoalsView.jsx';
import { isStandalone, openFile } from '../utils/openFile.js';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmt(n) { return n == null ? '-' : Number(n).toLocaleString('nl-NL'); }
function fmtMB(bytes) {
  if (!bytes) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes/1024).toFixed(1)} KB`;
  if (bytes < 1073741824) return `${(bytes/1048576).toFixed(1)} MB`;
  return `${(bytes/1073741824).toFixed(2)} GB`;
}
function fmtPct(n) { return n == null ? '-' : `${Number(n).toFixed(1)}%`; }
function fmtDays(n) { return n == null ? '-' : `${Math.round(n)} d`; }

function csvExport(url) {
  if (isStandalone()) return openFile(url);
  const a = document.createElement('a');
  a.href = url;
  a.click();
}

// ─── Stat card ────────────────────────────────────────────────────────────────

function StatCard({ icon, label, value, sub, color }) {
  return (
    <div style={`background:var(--color-surface);border:1px solid var(--color-border);border-radius:12px;padding:20px 22px;display:flex;align-items:center;gap:16px;border-left:4px solid ${color || 'var(--color-primary)'}`}>
      <div style={`font-size:28px;width:48px;height:48px;border-radius:12px;display:flex;align-items:center;justify-content:center;background:${color || 'var(--color-primary)'}18;flex-shrink:0`}>
        {icon}
      </div>
      <div>
        <div style="font-size:24px;font-weight:700;line-height:1">{value}</div>
        <div style="font-size:12px;font-weight:600;color:var(--color-text-muted);margin-top:3px">{label}</div>
        {sub && <div style="font-size:11px;color:var(--color-text-muted);margin-top:1px">{sub}</div>}
      </div>
    </div>
  );
}

// ─── Mini bar chart ───────────────────────────────────────────────────────────

function MiniBar({ value, max, color }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div style="display:flex;align-items:center;gap:8px">
      <div style="flex:1;height:6px;background:var(--color-border);border-radius:4px;overflow:hidden">
        <div style={`width:${pct}%;height:100%;background:${color || 'var(--color-primary)'};border-radius:4px;transition:width .4s`} />
      </div>
      <span style="font-size:11px;color:var(--color-text-muted);width:30px;text-align:right">{value}</span>
    </div>
  );
}

// ─── Activity sparkline ───────────────────────────────────────────────────────

function ActivitySparkline({ data }) {
  if (!data || data.length === 0) return (
    <div style="padding:32px;text-align:center;color:var(--color-text-muted);font-size:13px">Geen activiteit in de afgelopen 30 dagen</div>
  );

  const max = Math.max(...data.map(d => d.count), 1);
  const width = 600;
  const height = 80;
  const barW = Math.floor(width / 30) - 1;

  // Fill missing days
  const filled = [];
  for (let i = 29; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    const day = d.toISOString().slice(0, 10);
    const found = data.find(x => x.day === day);
    filled.push({ day, count: found ? found.count : 0 });
  }

  return (
    <div style="overflow-x:auto">
      <svg viewBox={`0 0 ${width} ${height + 20}`} style="width:100%;max-width:600px;display:block">
        {filled.map((d, i) => {
          const barH = Math.max(2, (d.count / max) * height);
          const x = i * (barW + 1);
          const y = height - barH;
          return (
            <g key={d.day}>
              <rect x={x} y={y} width={barW} height={barH}
                fill={d.count > 0 ? 'var(--color-primary)' : 'var(--color-border)'}
                rx="2" opacity="0.85">
                <title>{d.day}: {d.count} activiteiten</title>
              </rect>
              {i % 7 === 0 && (
                <text x={x} y={height + 14} font-size="9" fill="var(--color-text-muted)">
                  {d.day.slice(5)}
                </text>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// ─── Tab bar ──────────────────────────────────────────────────────────────────

function TabBar({ tabs, active, onChange }) {
  return (
    <div style="display:flex;gap:2px;background:var(--color-border);border-radius:10px;padding:3px;margin-bottom:24px;width:fit-content">
      {tabs.map(tab => (
        <button
          key={tab.id}
          onClick={() => onChange(tab.id)}
          style={`padding:7px 16px;border:none;border-radius:8px;cursor:pointer;font-size:13px;font-weight:500;transition:all .15s;background:${active === tab.id ? 'var(--color-surface)' : 'transparent'};color:${active === tab.id ? 'var(--color-text)' : 'var(--color-text-muted)'};box-shadow:${active === tab.id ? '0 1px 3px rgba(0,0,0,.1)' : 'none'}`}
        >
          {tab.icon} {tab.label}
        </button>
      ))}
    </div>
  );
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────

function TableSkeleton({ rows = 5, cols = 4 }) {
  return (
    <div style="padding:8px 0">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} style="display:flex;gap:16px;padding:12px 0;border-bottom:1px solid var(--color-border)">
          {Array.from({ length: cols }).map((_, j) => (
            <div key={j} class="skeleton" style={`height:13px;border-radius:4px;flex:${j === 0 ? 2 : 1}`} />
          ))}
        </div>
      ))}
    </div>
  );
}

// ─── Overview tab ─────────────────────────────────────────────────────────────

function OverviewTab({ seasonId }) {
  const [summary, setSummary] = useState(null);
  const [trend, setTrend] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = seasonId ? `?seasonId=${seasonId}` : '';
    Promise.all([
      api(`/api/reports/summary${params}`),
      api('/api/reports/activity-trend'),
    ]).then(([s, t]) => { setSummary(s); setTrend(t); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [seasonId]);

  if (loading) return <TableSkeleton rows={3} cols={3} />;
  if (!summary) return null;

  const { processes: p, tasks: tk, documents: d, members: m, coins: c } = summary;

  return (
    <div>
      {/* Stat cards */}
      <div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(200px,1fr));gap:16px;margin-bottom:28px">
        <StatCard icon="⚙️" label="Processen" value={fmt(p?.total)} sub={`${fmt(p?.active)} actief · ${fmt(p?.done)} afgerond`} color="#6366f1" />
        <StatCard icon="✅" label="Taken" value={fmt(tk?.total)} sub={`${fmt(tk?.open)} open · ${fmt(tk?.overdue)} verlopen`} color="#10b981" />
        <StatCard icon="📄" label="Documenten" value={fmt(d?.total)} sub={fmtMB(d?.total_size)} color="#f59e0b" />
        <StatCard icon="👥" label="Actieve leden" value={fmt(m?.total)} color="#3b82f6" />
        <StatCard icon="🪙" label="Coins uitgedeeld" value={fmt(c?.awarded)} color="#f97316" />
      </div>

      {/* Progress bars: process status */}
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:20px;margin-bottom:28px">
        <Card title="Proces status">
          <div style="display:flex;flex-direction:column;gap:12px">
            {[
              { label: '⚙️ Actief', value: p?.active, color: '#6366f1' },
              { label: '✅ Afgerond', value: p?.done, color: '#10b981' },
              { label: '❌ Geannuleerd', value: p?.cancelled, color: '#ef4444' },
            ].map(({ label, value, color }) => (
              <div key={label}>
                <div style="display:flex;justify-content:space-between;font-size:12px;margin-bottom:4px">
                  <span>{label}</span><span style="font-weight:600">{fmt(value)}</span>
                </div>
                <MiniBar value={value || 0} max={p?.total || 1} color={color} />
              </div>
            ))}
          </div>
        </Card>

        <Card title="Taak status">
          <div style="display:flex;flex-direction:column;gap:12px">
            {[
              { label: '📋 Open', value: tk?.open, color: '#3b82f6' },
              { label: '✅ Afgerond', value: tk?.done, color: '#10b981' },
              { label: '⚠️ Verlopen', value: tk?.overdue, color: '#f59e0b' },
            ].map(({ label, value, color }) => (
              <div key={label}>
                <div style="display:flex;justify-content:space-between;font-size:12px;margin-bottom:4px">
                  <span>{label}</span><span style="font-weight:600">{fmt(value)}</span>
                </div>
                <MiniBar value={value || 0} max={tk?.total || 1} color={color} />
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Activity trend */}
      <Card title="Activiteit afgelopen 30 dagen">
        <ActivitySparkline data={trend} />
      </Card>
    </div>
  );
}

// ─── Processes tab ────────────────────────────────────────────────────────────

function ProcessesTab({ seasonId }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = seasonId ? `?seasonId=${seasonId}` : '';
    api(`/api/reports/processes${params}`).then(setData).catch(() => setData([])).finally(() => setLoading(false));
  }, [seasonId]);

  const exportUrl = `/api/reports/export/processes${seasonId ? `?seasonId=${seasonId}` : ''}`;

  return (
    <div>
      <div style="display:flex;justify-content:flex-end;margin-bottom:16px">
        <Button variant="secondary" size="sm" onClick={() => csvExport(exportUrl)}>
          ⬇ Exporteer CSV
        </Button>
      </div>
      <Card title="Processen per sjabloon">
        {loading ? <TableSkeleton rows={5} cols={5} /> : data.length === 0 ? (
          <p style="padding:32px;text-align:center;color:var(--color-text-muted);font-size:13px">Geen procesdata beschikbaar</p>
        ) : (
          <div style="overflow-x:auto">
            <table style="width:100%;border-collapse:collapse;font-size:13px">
              <thead>
                <tr style="border-bottom:2px solid var(--color-border)">
                  {['Sjabloon','Totaal','Actief','Afgerond','Geannuleerd','Gem. doorlooptijd'].map(h => (
                    <th key={h} style="padding:10px 12px;text-align:left;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.05em;color:var(--color-text-muted)">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.map((row, i) => (
                  <tr key={i} style="border-bottom:1px solid var(--color-border)">
                    <td style="padding:10px 12px;font-weight:500">{row.template_name}</td>
                    <td style="padding:10px 12px">{fmt(row.total)}</td>
                    <td style="padding:10px 12px"><span style="color:#6366f1">{fmt(row.active)}</span></td>
                    <td style="padding:10px 12px"><span style="color:#10b981">{fmt(row.done)}</span></td>
                    <td style="padding:10px 12px"><span style="color:#ef4444">{fmt(row.cancelled)}</span></td>
                    <td style="padding:10px 12px;color:var(--color-text-muted)">{fmtDays(row.avg_days)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Process durations */}
      <ProcessDurationsSection />
    </div>
  );
}

function ProcessDurationsSection() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api('/api/reports/process-durations').then(setData).catch(() => setData([])).finally(() => setLoading(false));
  }, []);
  if (loading || data.length === 0) return null;
  return (
    <Card title="Doorlooptijden afgeronde processen" style="margin-top:20px">
      <div style="overflow-x:auto">
        <table style="width:100%;border-collapse:collapse;font-size:13px">
          <thead>
            <tr style="border-bottom:2px solid var(--color-border)">
              {['Sjabloon','Gem.','Min.','Max.','Aantal'].map(h => (
                <th key={h} style="padding:10px 12px;text-align:left;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.05em;color:var(--color-text-muted)">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row, i) => (
              <tr key={i} style="border-bottom:1px solid var(--color-border)">
                <td style="padding:10px 12px;font-weight:500">{row.processName}</td>
                <td style="padding:10px 12px">{fmtDays(row.avgDuration)}</td>
                <td style="padding:10px 12px;color:#10b981">{fmtDays(row.minDuration)}</td>
                <td style="padding:10px 12px;color:#ef4444">{fmtDays(row.maxDuration)}</td>
                <td style="padding:10px 12px;color:var(--color-text-muted)">{fmt(row.totalProcesses)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ─── Tasks tab ────────────────────────────────────────────────────────────────

function TasksTab() {
  const [byRole, setByRole] = useState([]);
  const [byUser, setByUser] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api('/api/reports/tasks-per-role'),
      api('/api/reports/tasks-per-user'),
    ]).then(([r, u]) => { setByRole(r); setByUser(u); })
      .catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div>
      <div style="display:flex;justify-content:flex-end;margin-bottom:16px">
        <Button variant="secondary" size="sm" onClick={() => csvExport('/api/reports/export/tasks')}>
          ⬇ Exporteer CSV
        </Button>
      </div>

      {/* Per user */}
      <Card title="Taken per lid">
        {loading ? <TableSkeleton rows={6} cols={5} /> : byUser.length === 0 ? (
          <p style="padding:32px;text-align:center;color:var(--color-text-muted);font-size:13px">Geen taakdata beschikbaar</p>
        ) : (
          <div style="overflow-x:auto">
            <table style="width:100%;border-collapse:collapse;font-size:13px">
              <thead>
                <tr style="border-bottom:2px solid var(--color-border)">
                  {['Lid','Totaal','Afgerond','Open','Verlopen','% Afgerond'].map(h => (
                    <th key={h} style="padding:10px 12px;text-align:left;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.05em;color:var(--color-text-muted)">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {byUser.map((row, i) => (
                  <tr key={i} style="border-bottom:1px solid var(--color-border)">
                    <td style="padding:10px 12px;font-weight:500">{row.name}</td>
                    <td style="padding:10px 12px">{fmt(row.total)}</td>
                    <td style="padding:10px 12px;color:#10b981">{fmt(row.done)}</td>
                    <td style="padding:10px 12px;color:#3b82f6">{fmt(row.open)}</td>
                    <td style="padding:10px 12px;color:#f59e0b">{fmt(row.overdue)}</td>
                    <td style="padding:10px 12px">
                      <div style="display:flex;align-items:center;gap:8px">
                        <div style="width:60px;height:5px;background:var(--color-border);border-radius:4px;overflow:hidden">
                          <div style={`width:${Math.min(100, row.pct_done || 0)}%;height:100%;background:#10b981;border-radius:4px`} />
                        </div>
                        <span style="font-size:12px">{fmtPct(row.pct_done)}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Per role */}
      <div style="margin-top:20px">
        <Card title="Taken per rol">
          {loading ? <TableSkeleton rows={4} cols={4} /> : byRole.length === 0 ? null : (
            <div style="overflow-x:auto">
              <table style="width:100%;border-collapse:collapse;font-size:13px">
                <thead>
                  <tr style="border-bottom:2px solid var(--color-border)">
                    {['Rol','Totaal','Afgerond','Open'].map(h => (
                      <th key={h} style="padding:10px 12px;text-align:left;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.05em;color:var(--color-text-muted)">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {byRole.map((row, i) => (
                    <tr key={i} style="border-bottom:1px solid var(--color-border)">
                      <td style="padding:10px 12px;font-weight:500">{row.role}</td>
                      <td style="padding:10px 12px">{fmt(row.totalTasks)}</td>
                      <td style="padding:10px 12px;color:#10b981">{fmt(row.completedTasks)}</td>
                      <td style="padding:10px 12px;color:#3b82f6">{fmt(row.openTasks)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

// ─── Members tab ──────────────────────────────────────────────────────────────

function MembersTab() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  // Names and e-mail addresses need the right to read members too (not only reports)
  const [noAccess, setNoAccess] = useState(false);

  useEffect(() => {
    api('/api/reports/members').then(setData)
      .catch(err => { setData([]); if (err?.code === 'insufficient_permissions') setNoAccess(true); })
      .finally(() => setLoading(false));
  }, []);

  const filtered = search
    ? data.filter(r => r.name?.toLowerCase().includes(search.toLowerCase()) || r.email?.toLowerCase().includes(search.toLowerCase()))
    : data;

  const availBadge = (av) => {
    const map = { beschikbaar: ['#10b981','Beschikbaar'], beperkt: ['#f59e0b','Beperkt'], onbeschikbaar: ['#ef4444','Onbeschikbaar'] };
    const [color, label] = map[av] || ['var(--color-text-muted)','—'];
    return <span style={`font-size:10px;padding:2px 7px;border-radius:10px;background:${color}20;color:${color};font-weight:600`}>{label}</span>;
  };

  if (noAccess) {
    return (
      <Card title="Leden">
        <p style="padding:32px;text-align:center;color:var(--color-text-muted);font-size:13px">
          Het ledenoverzicht toont namen en e-mailadressen. Daarvoor heb je naast rapportages ook het recht nodig om leden te bekijken.
        </p>
      </Card>
    );
  }

  return (
    <div>
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:16px;gap:12px">
        <div style="position:relative;flex:1;max-width:280px">
          <span style="position:absolute;left:9px;top:50%;transform:translateY(-50%);color:var(--color-text-muted);font-size:13px;pointer-events:none">🔍</span>
          <input
            class="form-input"
            value={search}
            onInput={e => setSearch(e.target.value)}
            placeholder="Zoek op naam of e-mail..."
            style="padding-left:30px;height:34px;font-size:13px"
          />
        </div>
        <Button variant="secondary" size="sm" onClick={() => csvExport('/api/reports/export/members')}>
          ⬇ Exporteer CSV
        </Button>
      </div>
      <Card title={`Leden (${filtered.length})`}>
        {loading ? <TableSkeleton rows={6} cols={5} /> : filtered.length === 0 ? (
          <p style="padding:32px;text-align:center;color:var(--color-text-muted);font-size:13px">Geen leden gevonden</p>
        ) : (
          <div style="overflow-x:auto">
            <table style="width:100%;border-collapse:collapse;font-size:13px">
              <thead>
                <tr style="border-bottom:2px solid var(--color-border)">
                  {['Naam','Beschikbaarheid','Rollen','Taken','Afgerond','Coins'].map(h => (
                    <th key={h} style="padding:10px 12px;text-align:left;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.05em;color:var(--color-text-muted)">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((row, i) => (
                  <tr key={i} style="border-bottom:1px solid var(--color-border)">
                    <td style="padding:10px 12px">
                      <div style="font-weight:500">{row.name}</div>
                      <div style="font-size:11px;color:var(--color-text-muted)">{row.email}</div>
                    </td>
                    <td style="padding:10px 12px">{availBadge(row.availability)}</td>
                    <td style="padding:10px 12px;font-size:12px;color:var(--color-text-muted);max-width:160px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap" title={row.roles}>{row.roles || '—'}</td>
                    <td style="padding:10px 12px">{fmt(row.task_count)}</td>
                    <td style="padding:10px 12px;color:#10b981">{fmt(row.tasks_done)}</td>
                    <td style="padding:10px 12px">
                      {row.coins_earned > 0
                        ? <span style="color:#f97316;font-weight:600">🪙 {fmt(row.coins_earned)}</span>
                        : <span style="color:var(--color-text-muted)">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

// ─── Documents tab ────────────────────────────────────────────────────────────

function DocumentsTab() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api('/api/reports/documents').then(setData).catch(() => setData([])).finally(() => setLoading(false));
  }, []);

  const total = data.reduce((s, r) => s + (r.total || 0), 0);
  const totalSize = data.reduce((s, r) => s + (r.total_size || 0), 0);

  return (
    <div>
      <div style="display:flex;justify-content:flex-end;margin-bottom:16px">
        <Button variant="secondary" size="sm" onClick={() => csvExport('/api/reports/export/documents')}>
          ⬇ Exporteer CSV
        </Button>
      </div>

      {/* Summary stats */}
      <div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(180px,1fr));gap:16px;margin-bottom:20px">
        <StatCard icon="📄" label="Totaal documenten" value={fmt(total)} color="#f59e0b" />
        <StatCard icon="💾" label="Totale opslagruimte" value={fmtMB(totalSize)} color="#6366f1" />
        <StatCard icon="📁" label="Actieve mappen" value={fmt(data.filter(r => r.folder !== '— Geen map —').length)} color="#10b981" />
      </div>

      <Card title="Documenten per map">
        {loading ? <TableSkeleton rows={5} cols={4} /> : data.length === 0 ? (
          <p style="padding:32px;text-align:center;color:var(--color-text-muted);font-size:13px">Geen documenten gevonden</p>
        ) : (
          <div style="overflow-x:auto">
            <table style="width:100%;border-collapse:collapse;font-size:13px">
              <thead>
                <tr style="border-bottom:2px solid var(--color-border)">
                  {['Map','Documenten','Opslagruimte','Laatste upload'].map(h => (
                    <th key={h} style="padding:10px 12px;text-align:left;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.05em;color:var(--color-text-muted)">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.map((row, i) => (
                  <tr key={i} style="border-bottom:1px solid var(--color-border)">
                    <td style="padding:10px 12px;font-weight:500">{row.folder}</td>
                    <td style="padding:10px 12px">
                      <div style="display:flex;align-items:center;gap:10px">
                        {fmt(row.total)}
                        <div style="width:80px;height:5px;background:var(--color-border);border-radius:4px;overflow:hidden">
                          <div style={`width:${Math.min(100,(row.total/total)*100)}%;height:100%;background:#f59e0b;border-radius:4px`} />
                        </div>
                      </div>
                    </td>
                    <td style="padding:10px 12px;color:var(--color-text-muted)">{fmtMB(row.total_size)}</td>
                    <td style="padding:10px 12px;color:var(--color-text-muted);font-size:12px">
                      {row.last_upload ? parseServerDate(row.last_upload).toLocaleDateString('nl-NL') : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

// ─── Main view ────────────────────────────────────────────────────────────────

// ─── PDCA tab ─────────────────────────────────────────────────────────────────

function rag(jp_total, jp_gedaan) {
  if (jp_total === 0) return { color: '#6b7280', bg: '#f3f4f6', label: 'Geen data' };
  const pct = jp_gedaan / jp_total;
  if (pct >= 0.75) return { color: '#16a34a', bg: '#dcfce7', label: 'Op schema' };
  if (pct >= 0.25) return { color: '#d97706', bg: '#fef3c7', label: 'Aandacht' };
  return { color: '#dc2626', bg: '#fee2e2', label: 'Achter' };
}

function ProgressBar({ value, total, color }) {
  const pct = total > 0 ? Math.min(100, Math.round((value / total) * 100)) : 0;
  return (
    <div style="display:flex;align-items:center;gap:8px;flex:1;min-width:0">
      <div style="flex:1;height:8px;background:var(--color-border);border-radius:4px;overflow:hidden">
        <div style={`width:${pct}%;height:100%;background:${color};border-radius:4px;transition:width .4s`} />
      </div>
      <span style="font-size:12px;font-weight:600;color:var(--color-text);width:34px;text-align:right;flex-shrink:0">{pct}%</span>
    </div>
  );
}

function CommissionRow({ c }) {
  const status = rag(c.jp_total, c.jp_gedaan);
  return (
    <div style="display:flex;align-items:center;gap:12px;padding:10px 14px;border-radius:8px;border:1px solid var(--color-border);background:var(--color-bg-secondary)">
      {/* RAG dot */}
      <div title={status.label} style={`width:10px;height:10px;border-radius:50%;background:${status.color};flex-shrink:0`} />

      {/* Name */}
      <div style="font-size:13px;font-weight:500;min-width:180px;flex-shrink:0">{c.commission_name}</div>

      {/* Progress bar */}
      <ProgressBar value={c.jp_gedaan} total={c.jp_total} color={status.color} />

      {/* Count label */}
      <div style="font-size:12px;color:var(--color-text-muted);white-space:nowrap;flex-shrink:0;width:80px;text-align:right">
        {c.jp_total === 0 ? '—' : `${c.jp_gedaan} / ${c.jp_total} gedaan`}
      </div>

      {/* Status pill */}
      <div style={`font-size:11px;font-weight:600;padding:2px 8px;border-radius:10px;background:${status.bg};color:${status.color};white-space:nowrap;flex-shrink:0`}>
        {status.label}
      </div>

      {/* Active processes */}
      {c.active_processes > 0 && (
        <div title="Actieve processen" style="font-size:11px;color:#6366f1;background:#ede9fe;padding:2px 7px;border-radius:10px;white-space:nowrap;flex-shrink:0">
          ⚙ {c.active_processes} {c.active_processes === 1 ? 'proces' : 'processen'}
        </div>
      )}
    </div>
  );
}

// PDCA per doel (Visie & doelen): status, linked work and measure per goal. Only while
// that feature is visible for this user.
function GoalsPdca({ seasonId }) {
  const auth = useAuth();
  const [data, setData] = useState(null);
  useEffect(() => {
    let stale = false;
    fetchFeatures(`${auth.userId}:${auth.activeClubId}`)
      .then(f => f.goals && api(`/api/goals${seasonId ? `?season_id=${seasonId}` : ''}`))
      .then(d => { if (!stale && d) setData(d); })
      .catch(() => {});
    return () => { stale = true; };
  }, [auth.userId, auth.activeClubId, seasonId]);
  const areas = (data?.focus_areas || []).map(a => ({ ...a, goals: a.goals.filter(g => !g.stopped_at) })).filter(a => a.goals.length);
  if (!areas.length) return null;
  return (
    <div style="margin-bottom:28px">
      <h3 style="font-size:14px;font-weight:700;margin:0 0 10px">🎯 Doelen</h3>
      {areas.map(a => (
        <div key={a.id} style="margin-bottom:14px">
          <div style="font-size:13px;font-weight:600;margin-bottom:6px">{a.title}</div>
          <div style="display:flex;flex-direction:column;gap:6px">
            {a.goals.map(g => {
              const st = GOAL_STATUS[g.status] || GOAL_STATUS.op_schema;
              const m = measurePct(g);
              return (
                <div key={g.id} style="display:flex;align-items:center;gap:12px;padding:10px 14px;border-radius:8px;border:1px solid var(--color-border);background:var(--color-bg-secondary);flex-wrap:wrap">
                  <div title={st.label} style={`width:10px;height:10px;border-radius:50%;background:${st.color};flex-shrink:0`} />
                  <div style="font-size:13px;font-weight:500;flex:1 1 200px;min-width:0">{g.title}</div>
                  <div style="flex:1 1 160px;min-width:120px;display:flex">
                    {g.progress?.total > 0
                      ? <ProgressBar value={g.progress.done} total={g.progress.total} color={st.color} />
                      : <span style="font-size:12px;color:var(--color-text-muted)">Geen gekoppeld werk</span>}
                  </div>
                  <div style="font-size:12px;color:var(--color-text-muted);white-space:nowrap;text-align:right">
                    {g.progress?.total > 0 ? `${g.progress.done} / ${g.progress.total} werk af` : '—'}{m !== null ? ` · ${m}% meetwaarde` : ''}
                  </div>
                  <div style={`font-size:11px;font-weight:600;padding:2px 8px;border-radius:10px;background:${st.bg};color:${st.color};white-space:nowrap`}>{st.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function PdcaTab({ seasonId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    api(`/api/reports/pdca${seasonId ? `?seasonId=${seasonId}` : ''}`)
      .then(setData)
      .catch(() => setError('Kon PDCA-data niet laden.'))
      .finally(() => setLoading(false));
  }, [seasonId]);

  if (loading) return <TableSkeleton rows={6} cols={4} />;
  if (error) return <div class="alert alert-error mt-4">{error}</div>;
  if (!data) return null;

  const totalSections = data.sections?.length || 0;
  const hasAnyJp = data.sections?.some(s => s.commissions.some(c => c.jp_total > 0));

  return (
    <div style="margin-top:20px">
      {/* Season + legend */}
      <div style="display:flex;align-items:center;justify-content:space-between;gap:12px;margin-bottom:20px;flex-wrap:wrap">
        <div style="font-size:13px;color:var(--color-text-muted)">
          {data.season ? <>Seizoen: <strong>{data.season.name}</strong></> : 'Geen actief seizoen'}
        </div>
        <div style="display:flex;align-items:center;gap:12px">
          {[
            { color: '#16a34a', bg: '#dcfce7', label: 'Op schema (≥75%)' },
            { color: '#d97706', bg: '#fef3c7', label: 'Aandacht (25–74%)' },
            { color: '#dc2626', bg: '#fee2e2', label: 'Achter (<25%)' },
            { color: '#6b7280', bg: '#f3f4f6', label: 'Geen data' },
          ].map(s => (
            <div key={s.label} style="display:flex;align-items:center;gap:5px">
              <div style={`width:10px;height:10px;border-radius:50%;background:${s.color};flex-shrink:0`} />
              <span style="font-size:11px;color:var(--color-text-muted)">{s.label}</span>
            </div>
          ))}
        </div>
      </div>

      <GoalsPdca seasonId={seasonId} />

      {!hasAnyJp && (
        <div style="text-align:center;padding:40px;color:var(--color-text-muted);font-size:13px;border:1px dashed var(--color-border);border-radius:12px;margin-bottom:16px">
          Geen jaarplanning toegewezen aan secties of commissies. Stel die in via <strong>Jaarplanning</strong>.
        </div>
      )}

      {data.sections?.map(section => (
        <div key={section.section_id} style="margin-bottom:24px">
          {/* Section header */}
          <div style="display:flex;align-items:center;gap:10px;margin-bottom:10px">
            <h3 style="font-size:14px;font-weight:700;margin:0">{section.section_name}</h3>
            {section.pijler && (
              <span style="font-size:11px;padding:2px 8px;border-radius:10px;background:var(--color-primary);color:#fff;font-weight:500">
                {section.pijler}
              </span>
            )}
          </div>

          {/* Commission rows */}
          <div style="display:flex;flex-direction:column;gap:6px">
            {section.commissions.map(c => (
              <CommissionRow key={c.commission_id} c={c} />
            ))}
          </div>
        </div>
      ))}

      {totalSections === 0 && (
        <div style="text-align:center;padding:40px;color:var(--color-text-muted);font-size:13px">
          Geen secties of commissies gevonden. Configureer deze eerst via de instellingen.
        </div>
      )}
    </div>
  );
}

const TABS = [
  { id: 'overview',   icon: '📊', label: 'Overzicht' },
  { id: 'processes',  icon: '⚙️', label: 'Processen' },
  { id: 'tasks',      icon: '✅', label: 'Taken' },
  { id: 'members',    icon: '👥', label: 'Leden' },
  { id: 'documents',  icon: '📄', label: 'Documenten' },
  { id: 'pdca',       icon: '🔄', label: 'PDCA' },
];

export default function ReportsView() {
  const [tab, setTab] = useState('overview');
  const [seasons, setSeasons] = useState([]);
  const [seasonId, setSeasonId] = useState('');

  useEffect(() => {
    api('/api/seasons').then(data => {
      setSeasons(data);
      const active = data.find(s => s.is_active);
      if (active) setSeasonId(String(active.id));
    }).catch(() => {});
  }, []);

  return (
    <div>
      <div class="page-header" style="margin-bottom:24px">
        <h1 class="page-title">📊 Rapportages &amp; exports</h1>
        {seasons.length > 0 && (
          <div style="display:flex;align-items:center;gap:8px">
            <label style="font-size:13px;color:var(--color-text-muted);font-weight:500">Seizoen:</label>
            <select
              class="form-input"
              value={seasonId}
              onChange={e => setSeasonId(e.target.value)}
              style="height:34px;font-size:13px;padding:0 10px;min-width:160px"
            >
              <option value="">Alle seizoenen</option>
              {seasons.map(s => (
                <option key={s.id} value={s.id}>
                  {s.name}{s.is_active ? ' ✓' : ''}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <TabBar tabs={TABS} active={tab} onChange={setTab} />

      {tab === 'overview'   && <OverviewTab  seasonId={seasonId || null} />}
      {tab === 'processes'  && <ProcessesTab seasonId={seasonId || null} />}
      {tab === 'tasks'      && <TasksTab />}
      {tab === 'members'    && <MembersTab />}
      {tab === 'documents'  && <DocumentsTab />}
      {tab === 'pdca'       && <PdcaTab seasonId={seasonId || null} />}
    </div>
  );
}
